import { useState, type FormEvent } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/base/StatusBadge";
import { LoadingState } from "@/components/base/LoadingState";
import { EmptyState } from "@/components/base/EmptyState";
import { Modal } from "@/components/base/Modal";
import { fmtDateTime, variantLabel } from "@/lib/format";
import { entryTypeLabel } from "@/lib/inventory";
import { firstRelation } from "@/lib/relations";
import { historicalProductName, type CatalogueHistoryFields } from "@/lib/catalogue-history";

interface Location {
  id: string;
  name: string;
  code: string | null;
  type: string;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  is_active: boolean;
}

interface BalanceRow {
  id: string;
  product_id: string;
  variant_id: string | null;
  on_hand: number;
  allocated: number;
  available: number;
  incoming: number;
  transferring: number;
  products?: { name: string; sku: string } | null;
  variants?: { sku: string | null; option_values: Record<string, string> | null } | null;
}

interface LedgerRow extends CatalogueHistoryFields {
  id: string;
  entry_type: string;
  qty_change: number;
  created_at: string;
  products?: { name: string } | null;
}

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

export default function LocationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { org } = useAuth();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: "",
    code: "",
    type: "warehouse",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
  });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["location-detail", org?.id, id],
    enabled: !!org?.id && !!id,
    queryFn: async () => {
      const [location, balances, ledger] = await Promise.all([
        supabase.from("locations").select("*").eq("org_id", org!.id).eq("id", id).maybeSingle(),
        supabase.from("inventory_balances").select("id, product_id, variant_id, on_hand, allocated, available, incoming, transferring, products(name, sku), variants(sku, option_values)").eq("org_id", org!.id).eq("location_id", id).order("product_id"),
        supabase.from("inventory_ledger").select("product_id, variant_id, catalogue_product_identity_id, catalogue_variant_identity_id, product_name_snapshot, sku_snapshot, catalogue_snapshot_origin, id, entry_type, qty_change, created_at, products(name)").eq("org_id", org!.id).eq("location_id", id).order("created_at", { ascending: false }).limit(20),
      ]);
      return {
        location: (location.data ?? null) as Location | null,
        balances: (balances.data ?? []).map((row) => ({
          ...row,
          products: firstRelation(row.products),
          variants: firstRelation(row.variants),
        })) as BalanceRow[],
        ledger: (ledger.data ?? []).map((row) => ({
          ...row,
          products: firstRelation(row.products),
        })) as LedgerRow[],
      };
    },
  });

  const openEdit = () => {
    if (!data?.location) return;
    const l = data.location;
    setForm({
      name: l.name,
      code: l.code ?? "",
      type: l.type,
      address_line1: l.address_line1 ?? "",
      address_line2: l.address_line2 ?? "",
      city: l.city ?? "",
      state: l.state ?? "",
      postal_code: l.postal_code ?? "",
      country: l.country ?? "",
      contact_name: l.contact_name ?? "",
      contact_email: l.contact_email ?? "",
      contact_phone: l.contact_phone ?? "",
    });
    setError("");
    setEditOpen(true);
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    const { error: updErr } = await supabase
      .from("locations")
      .update({
        name: form.name,
        code: form.code || null,
        type: form.type,
        address_line1: form.address_line1 || null,
        address_line2: form.address_line2 || null,
        city: form.city || null,
        state: form.state || null,
        postal_code: form.postal_code || null,
        country: form.country || null,
        contact_name: form.contact_name || null,
        contact_email: form.contact_email || null,
        contact_phone: form.contact_phone || null,
      })
      .eq("id", id);
    setSaving(false);
    if (updErr) {
      setError(updErr.message);
      return;
    }
    setEditOpen(false);
    queryClient.invalidateQueries({ queryKey: ["location-detail", org?.id, id] });
    queryClient.invalidateQueries({ queryKey: ["locations", org?.id] });
  };

  const toggleActive = async () => {
    if (!data?.location) return;
    setSaving(true);
    await supabase.from("locations").update({ is_active: !data.location.is_active }).eq("id", id);
    setSaving(false);
    queryClient.invalidateQueries({ queryKey: ["location-detail", org?.id, id] });
    queryClient.invalidateQueries({ queryKey: ["locations", org?.id] });
  };

  if (isLoading) return <LoadingState />;

  if (isError || !data?.location) {
    return (
      <div>
        <div className="mb-6">
          <button onClick={() => navigate("/locations")} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1 whitespace-nowrap">
            <i className="ri-arrow-left-line" /> Back to locations
          </button>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500 mb-4">Couldn't load this location.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">Retry</button>
        </div>
      </div>
    );
  }

  const l = data.location;
  const address = [l.address_line1, l.address_line2, l.city, l.state, l.postal_code, l.country].filter(Boolean).join(", ");
  const totalUnits = data.balances.reduce((sum, b) => sum + Number(b.on_hand ?? 0), 0);
  const totalAvailable = data.balances.reduce((sum, b) => sum + Number(b.available ?? 0), 0);

  return (
    <div>
      <div className="mb-6">
        <button onClick={() => navigate("/locations")} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1 whitespace-nowrap">
          <i className="ri-arrow-left-line" /> Back to locations
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
              <i className={l.type === "store" ? "ri-store-2-line text-emerald-600 text-xl" : "ri-building-2-line text-emerald-600 text-xl"} />
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-xl font-bold text-slate-900">{l.name}</h1>
                <StatusBadge status={l.is_active ? "active" : "inactive"} />
              </div>
              <p className="text-sm text-slate-500 mt-1">
                {l.code ? <span className="font-mono mr-3">{l.code}</span> : null}
                <span className="capitalize">{l.type}</span>
              </p>
              {address && <p className="text-sm text-slate-600 mt-2">{address}</p>}
              {(l.contact_name || l.contact_email || l.contact_phone) && (
                <p className="text-sm text-slate-500 mt-1">
                  {l.contact_name ?? "—"}{l.contact_email ? ` · ${l.contact_email}` : ""}{l.contact_phone ? ` · ${l.contact_phone}` : ""}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={openEdit} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2">
              <i className="ri-pencil-line" /> Edit
            </button>
            <button
              onClick={toggleActive}
              disabled={saving}
              className={`px-4 py-2 border text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2 disabled:opacity-60 ${
                l.is_active
                  ? "bg-white border-slate-200 hover:bg-red-50 hover:text-red-600 text-slate-700"
                  : "bg-emerald-600 hover:bg-emerald-700 text-white border-transparent"
              }`}
            >
              <i className={l.is_active ? "ri-close-circle-line" : "ri-checkbox-circle-line"} />
              {l.is_active ? "Deactivate" : "Activate"}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800">Inventory at this location</h3>
          </div>
          {data.balances.length === 0 ? (
            <EmptyState title="No stock" description="Stock at this location will appear here." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50/70 border-b border-slate-100">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">On hand</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Allocated</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Available</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Incoming</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Transferring</th>
                  </tr>
                </thead>
                <tbody>
                  {data.balances.map((b) => (
                    <tr key={b.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <Link to={`/products/${b.product_id}`} className="text-sm font-medium text-slate-800 hover:text-emerald-700 hover:underline">
                          {b.products?.name ?? "—"}
                          {b.variants && (variantLabel(b.variants.option_values) || b.variants.sku) && (
                            <span className="text-slate-500 font-normal ml-1">· {variantLabel(b.variants.option_values) || b.variants.sku}</span>
                          )}
                        </Link>
                        <p className="text-xs text-slate-400 font-mono">{b.products?.sku}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-800 text-right font-medium">{b.on_hand}</td>
                      <td className="px-4 py-3 text-sm text-slate-600 text-right">{b.allocated}</td>
                      <td className="px-4 py-3 text-sm text-emerald-700 text-right font-semibold">{b.available}</td>
                      <td className="px-4 py-3 text-sm text-slate-600 text-right">{b.incoming}</td>
                      <td className="px-4 py-3 text-sm text-slate-500 text-right font-medium">{b.transferring}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800">Recent movements</h3>
          </div>
          {data.ledger.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-slate-400">No movements recorded yet</div>
          ) : (
            <div className="divide-y divide-slate-50">
              {data.ledger.map((m) => (
                <div key={m.id} className="px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-800">
                      <span className="font-medium">{entryTypeLabel[m.entry_type] ?? m.entry_type}</span>
                      <span className="text-slate-400"> · {historicalProductName(m)}</span>
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">{fmtDateTime(m.created_at)}</p>
                  </div>
                  <span className={`text-sm font-semibold whitespace-nowrap ${m.qty_change >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                    {m.qty_change >= 0 ? "+" : ""}{m.qty_change}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit location"
        footer={
          <>
            <button onClick={() => setEditOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {saving ? "Saving..." : "Save changes"}
            </button>
          </>
        }
      >
        <form onSubmit={handleSave} className="space-y-4">
          {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Name *</label>
              <input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Code</label>
              <input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Type</label>
            <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} className={inputCls}>
              <option value="warehouse">Warehouse</option>
              <option value="store">Store</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Address line 1</label>
              <input value={form.address_line1} onChange={(e) => setForm((f) => ({ ...f, address_line1: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Address line 2</label>
              <input value={form.address_line2} onChange={(e) => setForm((f) => ({ ...f, address_line2: e.target.value }))} className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>City</label>
              <input value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>State</label>
              <input value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Postal code</label>
              <input value={form.postal_code} onChange={(e) => setForm((f) => ({ ...f, postal_code: e.target.value }))} className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Country</label>
            <input value={form.country} onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))} className={inputCls} />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Contact name</label>
              <input value={form.contact_name} onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Contact email</label>
              <input type="email" value={form.contact_email} onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Contact phone</label>
              <input value={form.contact_phone} onChange={(e) => setForm((f) => ({ ...f, contact_phone: e.target.value }))} className={inputCls} />
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
}
